import { useEffect, useRef } from "react";
import * as THREE from "three";

const PALETTES = {
  blue: { rim: 0x3b82f6, halo: 0x3b82f6, mesh: 0x3b82f6, nodes: 0x93c5fd, dot: 0x93c5fd, glow: 0x3b82f6, ring: 0x60a5fa },
  gold: { rim: 0x3b82f6, halo: 0x2f6fd6, mesh: 0xd4af5e, nodes: 0xe8d29a, dot: 0xf3e2b0, glow: 0xd4af5e, ring: 0xd4af5e },
  orange: { rim: 0xfa582d, halo: 0xd94a22, mesh: 0xfa582d, nodes: 0xffb08a, dot: 0xffd2bd, glow: 0xfa582d, ring: 0xff7a50 },
};

export const HeroGlobe = ({ className = "", accent = "blue" }) => {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    const P = PALETTES[accent] || PALETTES.blue;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setClearColor(0x000000, 0);
    el.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(38, 1, 0.1, 100);
    camera.position.set(0, 0, 3.6);

    const globe = new THREE.Group();
    globe.rotation.x = 0.35;
    globe.rotation.z = -0.15;
    scene.add(globe);

    const texture = new THREE.TextureLoader().load("/images/home/earth-night.webp");
    texture.colorSpace = THREE.SRGBColorSpace;
    const earth = new THREE.Mesh(new THREE.SphereGeometry(1, 64, 64), new THREE.MeshBasicMaterial({ map: texture }));
    globe.add(earth);

    const rimShader = (color, side, fragment) =>
      new THREE.ShaderMaterial({
        transparent: true,
        side,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        uniforms: { c: { value: new THREE.Color(color) } },
        vertexShader: "varying vec3 vN; void main(){ vN = normalize(normalMatrix * normal); gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }",
        fragmentShader: fragment,
      });

    const rim = new THREE.Mesh(
      new THREE.SphereGeometry(1.005, 64, 64),
      rimShader(P.rim, THREE.FrontSide, "uniform vec3 c; varying vec3 vN; void main(){ float i = pow(1.0 - abs(dot(vN, vec3(0.0,0.0,1.0))), 2.5); gl_FragColor = vec4(c, 1.0) * i * 0.9; }")
    );
    globe.add(rim);

    const mesh = new THREE.LineSegments(
      new THREE.WireframeGeometry(new THREE.IcosahedronGeometry(1.14, 2)),
      new THREE.LineBasicMaterial({ color: P.mesh, transparent: true, opacity: 0.14 })
    );
    globe.add(mesh);

    const nodes = new THREE.Points(new THREE.IcosahedronGeometry(1.14, 2), new THREE.PointsMaterial({ color: P.nodes, size: 0.022, transparent: true, opacity: 0.85 }));
    globe.add(nodes);

    const halo = new THREE.Mesh(
      new THREE.SphereGeometry(1.06, 64, 64),
      rimShader(P.halo, THREE.BackSide, "uniform vec3 c; varying vec3 vN; void main(){ float i = pow(0.66 - dot(vN, vec3(0.0,0.0,1.0)), 3.0); gl_FragColor = vec4(c, 1.0) * i * 2.2; }")
    );
    scene.add(halo);

    const lat = 9.93, lon = 76.27;
    const phi = (90 - lat) * (Math.PI / 180);
    const theta = (lon + 180) * (Math.PI / 180);
    const pos = new THREE.Vector3(-Math.sin(phi) * Math.cos(theta), Math.cos(phi), Math.sin(phi) * Math.sin(theta));
    const marker = new THREE.Group();
    marker.position.copy(pos.clone().multiplyScalar(1.004));
    marker.lookAt(pos.clone().multiplyScalar(2));
    const dot = new THREE.Mesh(new THREE.SphereGeometry(0.018, 16, 16), new THREE.MeshBasicMaterial({ color: P.dot }));
    const dotGlow = new THREE.Mesh(new THREE.SphereGeometry(0.034, 16, 16), new THREE.MeshBasicMaterial({ color: P.glow, transparent: true, opacity: 0.45, blending: THREE.AdditiveBlending, depthWrite: false }));
    const rings = [0, 0.5].map((offset) => {
      const ring = new THREE.Mesh(new THREE.RingGeometry(0.03, 0.036, 48), new THREE.MeshBasicMaterial({ color: P.ring, transparent: true, opacity: 0.9, side: THREE.DoubleSide, depthWrite: false }));
      ring.userData.offset = offset;
      return ring;
    });
    marker.add(dot, dotGlow, ...rings);
    earth.add(marker);
    earth.rotation.y = THREE.MathUtils.degToRad(75) - theta;

    const resize = () => {
      const s = Math.min(el.clientWidth, el.clientHeight) || el.clientWidth;
      renderer.setSize(s, s, false);
      renderer.domElement.style.width = `${s}px`;
      renderer.domElement.style.height = `${s}px`;
    };
    resize();

    let raf;
    let visible = true;
    const clock = new THREE.Clock();
    const frame = () => {
      const dt = clock.getDelta();
      const t = clock.elapsedTime;
      earth.rotation.y += dt * 0.07;
      mesh.rotation.y -= dt * 0.04;
      nodes.rotation.y -= dt * 0.04;
      for (const ring of rings) {
        const p = (t * 0.7 + ring.userData.offset) % 1;
        ring.scale.setScalar(1 + p * 3.2);
        ring.material.opacity = (1 - p) * 0.9;
      }
      renderer.render(scene, camera);
      if (!reduced && visible) raf = requestAnimationFrame(frame);
    };
    frame();

    const onVis = () => {
      visible = document.visibilityState === "visible";
      if (visible && !reduced) { clock.getDelta(); raf = requestAnimationFrame(frame); }
    };
    document.addEventListener("visibilitychange", onVis);
    const ro = new ResizeObserver(resize);
    ro.observe(el);

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("visibilitychange", onVis);
      ro.disconnect();
      renderer.dispose();
      texture.dispose();
      el.removeChild(renderer.domElement);
    };
  }, [accent]);

  return <div ref={ref} className={`flex items-center justify-center ${className}`} aria-hidden="true" data-testid="home-hero-globe" />;
};
